/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */

import moment from 'moment';
import _ from 'lodash';
import find from 'lodash/find';
import map from 'lodash/map';
import { compact, difference, uniq, without } from 'lodash/array';

import * as Validator from 'common/utils/Validator';
import * as ApiService from 'common/services/ApiService';
import * as ClientService from 'common/services/ClientService';
import {
    BUSINESS_SEGMENT,
    CLIENT_SETTINGS,
    LINE_OF_BUSINESS,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { generateRandomUUID } from 'common/utils/Utils';

export function getClients(filterQuery, hierarchy = false) {
    const filterFields = {
        filter: {
            include: [
                {
                    relation: 'motiliRep',
                    scope: {
                        fields: ['firstName', 'lastName', 'fullName'],
                    },
                },
                {
                    relation: 'address',
                    scope: {
                        fields: [
                            'stdDeliveryLine',
                            'stdCity',
                            'stdRegion',
                            'stdPostalCode',
                            'stdPostalCodePlus4',
                            'stdAddress',
                            'stdCountry',
                            'standardized',
                        ],
                    },
                },
            ],
            fields: [
                'id',
                'name',
                'statusId',
                'doingBusinessAs',
                'website',
                'phone',
                'addressId',
                'billingAddressId',
                'contact',
                'motiliRepId',
                'directSupply',
                'operationInCountry',
                'currency',
            ],
        },
    };
    const query = _.merge({}, filterFields, { filter: filterQuery });
    return hierarchy
        ? ClientService.findHierarchy(query)
        : ClientService.find(query);
}

/**
 * @return {Promise<Array>} of clients flagged true for direct supply
 */
export async function getDirectSupplyClients() {
    const query = {
        filter: {
            fields: [
                'id',
                'name',
                'statusId',
                'doingBusinessAs',
                'directSupply',
                'operationInCountry',
            ],
            include: [
                {
                    relation: 'contractorOrderingAccount',
                    scope: { fields: ['clientPaymentAccountId'] },
                },
            ],
            where: {
                directSupply: true,
                statusId: {
                    inq: ['APPROVED', 'ENABLED'],
                },
            },
            order: ['name ASC'],
        },
    };
    const clients = await ClientService.find(query);
    return clients.filter(client => !client.contractorOrderingAccount);
}

export async function getClientAgents(clientId) {
    const query = {
        filter: {
            fields: ['id'],
            include: [
                {
                    relation: 'agents',
                },
            ],
        },
    };

    const client = await ClientService.findById(clientId, query);
    return client.agents;
}

/**
 * Check all clients' association to clientSettings to see if passed in settingType(s)
 * exists for that client.
 * Return array of clientId who have the passed in settingType(s)
 * @param {array} settingTypes
 * @param {array} lobSettingTypes
 * @return {array} clientIds
 */
export async function getClientIdBySettingsType(
    settingTypes,
    lobSettingTypes,
    retailStoreInfo = []
) {
    const clientFilter = {
        filter: {
            include: [
                {
                    relation: 'settings',
                    scope: {
                        fields: ['clientId', 'setting'],
                        where: {
                            settingTypeId: 'LINE_OF_BUSINESS',
                        },
                    },
                },
            ],
            fields: ['id'],
            order: ['id ASC'],
        },
    };

    const query = {
        where: {
            and: [],
        },
    };

    retailStoreInfo.forEach(filter => {
        switch (filter.target) {
            case 'retailRegion':
                query.where.and.push({
                    region: { ilike: `${filter.term}%` },
                });
                break;
            case 'retailDistrict':
                query.where.and.push({
                    district: { ilike: `${filter.term}%` },
                });
                break;
            case 'retailStoreNumber':
                query.where.and.push({
                    storeNumber: { ilike: `${filter.term}%` },
                });
                break;
            default:
        }
    });

    let queryFilter = clientFilter;

    if (retailStoreInfo.length > 0) {
        queryFilter = _.merge({}, clientFilter, { filter: query });
    }

    const clientWithSettings = await ClientService.find(queryFilter);
    let matchClients = clientWithSettings;

    if (lobSettingTypes.length > 0) {
        matchClients = clientWithSettings.filter(({ settings }) => {
            const { lineOfBusiness } =
                settings && settings[0] && settings[0].setting
                    ? settings[0].setting
                    : {};
            return lobSettingTypes.find(({ id }) => id === lineOfBusiness);
        });
    }
    if (settingTypes.length > 0) {
        matchClients = matchClients.filter(({ settings }) => {
            const { segment } =
                settings && settings[0] && settings[0].setting
                    ? settings[0].setting
                    : {};
            return settingTypes.find(({ segmentId }) => segmentId === segment);
        });
    }
    return matchClients.map(c => c.id);
}

export function getCountOfClients(query) {
    return ClientService.count(query);
}

export function getClientsCount(filteredQuery) {
    const query = {
        where: {
            and: [],
        },
    };

    if (filteredQuery !== undefined) {
        query.where = filteredQuery;
    }

    return ApiService.post(true, 'Clients/search/count', query);
}

export async function processFilters(activeFilters, activeQuery) {
    const query = _.merge({}, activeQuery, {
        where: {
            and: [],
        },
    });

    if (!activeFilters.length) {
        return query;
    }

    const statusFilter = { or: [] };
    const stdRegions = [];
    const stdCountrys = [];

    activeFilters.forEach(filter => {
        switch (filter.target) {
            // search filters
            case 'ADDRESS':
                query.where.and.push({ addressId: { inq: filter.term } });
                break;
            case 'CITY':
                query.where.and.push({ addressId: { inq: filter.term } });
                break;
            case 'CLIENT':
                query.where.and.push({ name: { ilike: `${filter.term}%` } });
                break;
            case 'MOTILI_REP':
                query.where.and.push({ motiliRepId: { inq: filter.term } });
                break;
            case 'ZIP':
                query.where.and.push({ addressId: { inq: filter.term } });
                break;
            // advanced filters
            case 'status':
                statusFilter.or.push({ statusId: filter.term });
                break;
            case 'country':
                stdCountrys.push(filter.term);
                break;
            case 'state':
                stdRegions.push(filter.term);
                break;
            case 'autoInvoiceEnable':
                query.where.and.push({ autoInvoiceEnable: filter.term });
                break;
            case 'operationInCountry':
                query.where.and.push({ operationInCountry: filter.term });
                break;
            default:
        }
    });
    if (stdRegions.length > 0) {
        const companyAddresses = await getCompanyAddresses({
            fields: 'id',
            where: { stdRegion: { inq: stdRegions } },
        });
        query.where.and.push({
            addressId: { inq: companyAddresses.map(a => a.id) },
        });
    }

    if (stdCountrys.length > 0) {
        const companyAddresses = await getCompanyAddresses({
            fields: 'id',
            where: { stdCountry: { inq: stdCountrys } },
        });
        query.where.and.push({
            addressId: { inq: companyAddresses.map(a => a.id) },
        });
    }

    query.where.and.push(statusFilter);

    return query;
}

export function getClient(id) {
    const query = {
        filter: {
            include: [
                {
                    relation: 'parent',
                    scope: {
                        include: ['address', 'billingAddress'],
                        fields: [
                            'id',
                            'parentClientId',
                            'statusId',
                            'name',
                            'doingBusinessAs',
                            'website',
                            'phone',
                            'phoneCountry',
                            'fax',
                            'internalNotificationEmail',
                            'contact',
                            'notes',
                            'approvalDate',
                            'addressId',
                            'billingAddressId',
                            'motiliRepId',
                            'created',
                            'updated',
                            'termNet',
                            'directSupply',
                            'taxable',
                            'autoInvoiceEnable',
                        ],
                    },
                },
                {
                    relation: 'address',
                    scope: {
                        fields: [
                            'rawAddress',
                            'stdAddress',
                            'stdDeliveryLine',
                            'stdCity',
                            'stdRegion',
                            'stdPostalCode',
                            'stdPostalCodePlus4',
                            'standardized',
                            'timezone',
                            'latitude',
                            'longitude',
                            'stdCountry',
                        ],
                    },
                },
                {
                    relation: 'billingAddress',
                    scope: {
                        fields: [
                            'rawAddress',
                            'stdAddress',
                            'stdDeliveryLine',
                            'stdCity',
                            'stdRegion',
                            'stdPostalCode',
                            'stdPostalCodePlus4',
                            'standardized',
                            'timezone',
                            'latitude',
                            'longitude',
                            'stdCountry',
                        ],
                    },
                },
                {
                    relation: 'team',
                    scope: {
                        fields: ['accountId'],
                        include: [
                            {
                                relation: 'account',
                                scope: {
                                    fields: [
                                        'id',
                                        'active',
                                        'firstName',
                                        'lastName',
                                        'timezoneId',
                                        'email',
                                        'notificationPreferences',
                                        'createdAt',
                                    ],
                                    include: [
                                        'childClientPermissions',
                                        {
                                            relation: 'roles',
                                            scope: {
                                                fields: ['name', 'description'],
                                            },
                                        },
                                    ],
                                },
                            },
                        ],
                    },
                },
                {
                    relation: 'requirements',
                },
                {
                    relation: 'blacklists',
                    scope: {
                        include: {
                            relation: 'contractor',
                            scope: {
                                fields: ['name'],
                            },
                        },
                        fields: ['reasonId', 'contractorId', 'id'],
                    },
                },
                {
                    relation: 'settings',
                },
                {
                    relation: 'integrations',
                },
                {
                    relation: 'notificationPreferences',
                },
                {
                    relation: 'clientOrderRules',
                },
                {
                    relation: 'clientUtilities',
                },
                {
                    relation: 'vendorInventoryLocations',
                    scope: {
                        include: [
                            {
                                relation: 'vendor',
                                scope: {
                                    fields: ['id', 'name', 'doingBusinessAs'],
                                },
                            },
                        ],
                    },
                },
                { relation: 'files' },
                {
                    relation: 'agents',
                    scope: { order: ['lastName ASC', 'firstName ASC'] },
                },
                { relation: 'contacts' },
                { relation: 'vendorOrderRules' },
                {
                    relation: 'preferredContractor',
                    scope: {
                        include: {
                            relation: 'contractor',
                            scope: {
                                fields: ['id', 'name'],
                            },
                        },
                        fields: [
                            'id',
                            'preferredContractorId',
                            'priority',
                            'dispatchingRadius',
                        ],
                    },
                },
                { relation: 'warrantyOwners' },
                { relation: 'clientTSMAssignmentEvent' },
                { relation: 'contractorOrderingAccount' },
            ],
            fields: [
                'id',
                'parentClientId',
                'statusId',
                'name',
                'doingBusinessAs',
                'website',
                'phone',
                'phoneCountry',
                'fax',
                'internalNotificationEmail',
                'contact',
                'notes',
                'approvalDate',
                'addressId',
                'billingAddressId',
                'motiliRepId',
                'created',
                'updated',
                'termNet',
                'directSupply',
                'taxable',
                'autoInvoiceEnable',
                'filterProductViewEnabled',
                'showVendorActivity',
                'agentBilling',
                'inheritedCompanyAddress',
                'inheritedBillingAddress',
                'inheritedWebsite',
                'inheritedPhotoRequirement',
                'inheritedAssetRequirement',
                'inheritedSpecialInstructions',
                'inheritedVideoRequirement',
                'liftGate',
                'liftGateText',
                'businessChannel',
                'uiOrderAllowPickupEquipment',
                'uiOrderAllowDeliveryEquipment',
                'uiOrderAllowPickupParts',
                'uiOrderAllowDeliveryParts',
                'demographics',
                'creditLimit',
                'temporaryCreditLimit',
                'temporaryCreditLimitStartDate',
                'temporaryCreditLimitEndDate',
                'inheritedStatusChangedNotification',
                'region',
                'district',
                'storeNumber',
                'enabledDynamicDispatching',
                'enabledParentDynamicDispatching',
                'enabledExclusiveDispatching',
                'enabledAutoDispatch',
                'autoDispatchResumeDate',
                'operationInCountry',
                'currency',
                'inheritedBundleProductsEnable',
                'bundleProductsEnable',
                'homeownerPaymentEnable',
                'ffScheduler',
                'enabledParentWorkOrderCheckInReminder',
                'rebateEnabled',
                'rebateAmount',
                'tsmId',
                'mincronId',
                'enabledVideoRecording',
                'inheritedEnabledVideoRecording',
                'dispatchingType',
                'enabledExclusiveContractorDispatching',
            ],
        },
    };
    return ClientService.findById(id, query);
}

export function getClientAutoInvoiceInfo(id) {
    const query = {
        filter: {
            fields: ['id', 'statusId', 'autoInvoiceEnable'],
            include: [{ relation: 'integrations' }],
        },
    };
    return ClientService.findById(id, query);
}

export function getClientExclusiveDispatching(id) {
    const query = {
        filter: {
            fields: ['id', 'enabledExclusiveDispatching'],
        },
    };
    return ClientService.findById(id, query);
}

export function getClientDispatchingConfig(id) {
    const query = {
        filter: {
            fields: ['id', 'enabledExclusiveContractorDispatching'],
            include: [
                {
                    relation: 'preferredContractor',
                    scope: {
                        fields: ['id', 'preferredContractorId'],
                        include: {
                            relation: 'contractor',
                            scope: { fields: ['id'] },
                        },
                    },
                },
            ],
        },
    };
    return ClientService.findById(id, query);
}

export function getCompanyAddresses(query) {
    return ApiService.get(true, 'CompanyAddresses', { filter: query });
}

/**
 * invokes POST /api/Clients
 * @param {object} client
 * @param {object} [query]
 * @returns {Promise<Object>}
 */
export function create(client, query) {
    return ClientService.create(client, query);
}

export function update(clientId, value) {
    return ClientService.patch(clientId, value);
}

export function updateDispatchingConfigs(clientId, data) {
    return ClientService.updateDispatchingConfigs(clientId, data);
}

export function createNote(id, note, mentionsData) {
    const _note = _.assign(
        {},
        {
            message: note,
            mentionsData,
        }
    );
    return ClientService.createNote(id, _note);
}

export function updateNote(id, note) {
    const _note = _.assign(
        {},
        {
            message: note.message,
            ...note,
        }
    );
    return ClientService.updateNote(id, note.id, _note);
}

export function activateTeamMember(id, teamMemberId) {
    return ClientService.activateTeamMember(id, teamMemberId).then(() =>
        getClient(id)
    );
}

export function deactivateTeamMember(id, teamMemberId) {
    return ClientService.deactivateTeamMember(id, teamMemberId).then(() =>
        getClient(id)
    );
}

export function createTeamMember(id, teamMember) {
    return ClientService.createTeamMember(id, teamMember).then(() =>
        getClient(id)
    );
}

export function updateTeamMember(id, teamMember) {
    return ClientService.updateTeamMember(id, teamMember).then(() =>
        getClient(id)
    );
}

/**
 *
 * @param {number} id
 * @param {object} requirement
 * @returns {Promise<Object>}
 */
export function createRequirement(id, requirement) {
    return ClientService.createRequirement(id, requirement);
}

/**
 *
 * @param {number} id
 * @param {number} requirementId
 * @param {object} requirement
 * @returns {Promise<Object>}
 */
export function updateRequirement(id, requirementId, requirement) {
    return ClientService.updateRequirement(id, requirementId, requirement);
}

export function updateInheritedVideoRequirement(id, data) {
    return ClientService.updateInheritedVideoRequirement(id, data);
}

export function updateEnabledVideoRecording(id, data) {
    return ClientService.updateEnabledVideoRecording(id, data);
}

/**
 *
 * @param {number} id
 * @param {number} requirementId
 */
export function deleteRequirement(id, requirementId) {
    return ClientService.deleteRequirement(id, requirementId);
}

export function inheritedPhotoRequirement(id, requirementId) {
    return ClientService.inheritedPhotoRequirement(id, requirementId);
}

export function inheritedAssetRequirement(id, requirementId) {
    return ClientService.inheritedAssetRequirement(id, requirementId);
}

export function createAgent(id, agent) {
    return ClientService.createAgent(id, agent);
}

export function updateAgent(id, agentId, agent) {
    return ClientService.updateAgent(id, agentId, agent);
}

/**
 *
 * @param {number} id
 * @param {number} settingId
 * @param {object} setting
 * @returns {Promise<Object>}
 */
export function updateSetting(id, settingId, setting) {
    return ClientService.updateSetting(id, settingId, setting);
}

/**
 *
 * @param {number} clientId
 * @param {object} setting
 * @returns {Promise<Object>}
 */
export function createSetting(clientId, setting) {
    return ClientService.createSetting(clientId, setting);
}

/**
 *
 * @param {number} clientId
 * @param {object} integration
 * @returns {Promise<Object>}
 */
export function createIntegration(clientId, integration) {
    return ClientService.createIntegration(clientId, integration);
}

/**
 *
 * @param {number} clientId
 * @param {string} integrationId
 * @param {object} integration
 * @returns {Promise<Object>}
 */
export function updateIntegration(clientId, integrationId, integration) {
    return ClientService.updateIntegration(
        clientId,
        integrationId,
        integration
    );
}

/**
 *
 * @param {number} clientId
 * @param {object} integrationId
 * @returns {Promise<Object>}
 */
export function deleteIntegration(clientId, integrationId) {
    return ClientService.deleteIntegration(clientId, integrationId);
}

export function approve(clientId, reason, childrenIds) {
    return ClientService.approve(clientId, reason, childrenIds);
}

export function deactivate(clientId, reason, childrenIds) {
    return ClientService.deactivate(clientId, reason, childrenIds);
}

export function hold(clientId, reason, childrenIds) {
    return ClientService.hold(clientId, reason, childrenIds);
}

export function enable(clientId, reason, childrenIds) {
    return ClientService.enable(clientId, reason, childrenIds);
}

export function pending(clientId, reason, childrenIds) {
    return ClientService.pending(clientId, reason, childrenIds);
}

export function estimateCancelOrder(clientId, autoCancellationDurations) {
    return ClientService.estimateCancelOrder(
        clientId,
        autoCancellationDurations
    );
}

/**
 * validates a client address object
 * @param {object} address
 * @returns {boolean}
 */
export function isValidAddress(address) {
    return ClientService.isValidAddress(address);
}

/**
 *
 * @param {object} contact
 * @returns {boolean}
 */
export function isValidContact(contact) {
    return ClientService.isValidContact(contact);
}

/**
 * validates a client object for use with creation (POST /api/clients)
 * @param {object} client
 * @returns {boolean}
 */
export function isValidClientCreationForm(client) {
    if (!client) {
        return false;
    }
    if (!client.name) {
        return false;
    }
    if (!Validator.isMax100Characters(client.name)) {
        return false;
    }
    if (!client.doingBusinessAs) {
        return false;
    }
    if (!client.motiliRepId) {
        return false;
    }
    if (!client.address) {
        return false;
    }
    if (!client.termNet) {
        return false;
    }
    if (!isValidTerms(client.termNets)) {
        return false;
    }
    if (!ClientService.isValidAddress(client.address)) {
        return false;
    }
    if (!client.useBillingAsCompanyAddress && !client.billingAddress) {
        return false;
    }
    if (
        !client.useBillingAsCompanyAddress &&
        !ClientService.isValidAddress(client.billingAddress)
    ) {
        return false;
    }
    if (
        client.internalNotificationEmail &&
        !Validator.isMotiliEmail(client.internalNotificationEmail)
    ) {
        return false;
    }
    if (!client.lineOfBusiness) {
        return false;
    }
    if (!client.segment) {
        return false;
    }
    if (client.phone && !client.validPhone) {
        return false;
    }
    if (
        client.contact &&
        (client.contact.name ||
            client.contact.email ||
            client.contact.phone ||
            client.contact.mobile)
    ) {
        if (!ClientService.isValidContact(client.contact)) {
            return false;
        }
    }
    if (
        client.contact &&
        client.contact.email &&
        !Validator.isEmail(client.contact.email)
    ) {
        return false;
    }
    if (client.contact && client.contact.phone) {
        if (!Validator.isPhone(client.contact.phone)) {
            return false;
        }
        if (!client.contact.validPhone) {
            return false;
        }
    }
    if (client.contact && client.contact.mobile) {
        if (!Validator.isPhone(client.contact.mobile)) {
            return false;
        }
        if (!client.contact.validMobile) {
            return false;
        }
    }
    if (!client.operationInCountry) {
        return false;
    }

    return true;
}

export function isEquipmentOnly(settings) {
    const clientLOB = _.find(settings, {
        settingTypeId: CLIENT_SETTINGS.LINE_OF_BUSINESS,
    });
    return clientLOB?.setting?.lineOfBusiness === LINE_OF_BUSINESS.EQUIPMENT;
}

export function getTermNetSettings(settings) {
    const equipmentOnlu = isEquipmentOnly(settings);
    if (!settings || !equipmentOnlu) {
        return [];
    }
    const termNetSettings = _.filter(settings, {
        settingTypeId: CLIENT_SETTINGS.TERM_NET,
    });
    return _.orderBy(
        map(termNetSettings, t => ({
            ...t.setting,
            id: generateRandomUUID(),
        })),
        ['threshold'],
        ['desc']
    );
}

export function isValidTerms(terms) {
    if (!terms || !terms.length) {
        return true;
    }
    if (
        _.some(
            terms,
            term => !term.termNet || !(parseFloat(term.threshold) > 0)
        )
    ) {
        return false;
    }
    const thresholds = terms.map(term =>
        _.round(parseFloat(term.threshold), 2)
    );
    return thresholds.length === uniq(thresholds).length;
}

export function mapTerms(terms) {
    return _.map(terms, term => ({
        termNet: term.termNet,
        threshold: parseFloat(term.threshold),
    }));
}

/**
 * validates the temporary credit limit fields as a group - when a temporary
 * credit limit amount is set, both a start and end date are required and the
 * end date must not precede the start date.
 * @param {number|string} temporaryCreditLimit
 * @param {string} startDate
 * @param {string} endDate
 * @returns {boolean}
 */
export function isValidTemporaryCreditLimit(
    temporaryCreditLimit,
    startDate,
    endDate
) {
    if (temporaryCreditLimit === undefined || temporaryCreditLimit === null) {
        return true;
    }
    if (!(parseFloat(temporaryCreditLimit) > 0)) {
        return false;
    }
    if (!startDate || !endDate) {
        return false;
    }
    return !moment(endDate).isBefore(moment(startDate));
}

export function hasClientRetailDetails(businessSegment) {
    return businessSegment === BUSINESS_SEGMENT.RETAIL;
}

export function deDuplicateEmails(emails, withOut) {
    const uniqEmails = uniqEmail(emails);
    const withOutEmails = uniqEmail(withOut);
    return without
        ? difference(uniqEmails, withOutEmails).join(', ')
        : uniqEmails.join(', ');
}

function uniqEmail(emails) {
    const mailArray = emails ? emails.split(/[;,]/) : [];
    const trimMail = map(mailArray, function (email) {
        return email.trim();
    });
    return compact(uniq(trimMail));
}

export function getOnHoldRelateDocument(id, includeSubClient = false) {
    return ClientService.getOnHoldRelateDocument(id, includeSubClient);
}

export function getClientWorkRequestUrl(clientId, UUID) {
    return ClientService.getClientWorkRequestUrl(clientId, UUID);
}

export function generateUtilityUrl(clientId) {
    return ClientService.generateUtilityUrl(clientId);
}

export function listParent(clientId) {
    return ApiService.get(true, `clients/listAvailableParent`, {
        clientId,
    }).then(clients => _.sortBy(clients, 'display'));
}

export function getClientTree(clientId) {
    return ApiService.get(true, `clients/${clientId}/tree`);
}

export function convertParentChild(clientId, data) {
    return ApiService.post(
        true,
        `clients/${clientId}/convertParentChild`,
        data
    );
}

export function convertToParent(clientId, data) {
    return ApiService.post(true, `clients/${clientId}/convertToParent`, data);
}

export function validateTeamMember(clientId, data) {
    return ApiService.post(
        true,
        `clients/${clientId}/validateTeamMember`,
        data
    );
}

export function findClientFromClientTree(clientTree, id) {
    if (!clientTree) {
        return null;
    }
    if (clientTree.id === id) {
        return clientTree;
    }
    if (!clientTree.children || clientTree.children.length === 0) {
        return null;
    }
    for (let i = 0; i < clientTree.children.length; i++) {
        const found = findClientFromClientTree(clientTree.children[i], id);
        if (found) {
            return found;
        }
    }
    return null;
}

export function validateClientStatus(client, parent) {
    const { statusId } = client;
    if (statusId && parent?.statusId) {
        if (
            statusId === 'INACTIVE' &&
            ![
                'INACTIVE',
                'PENDING_APPROVAL',
                'ON_HOLD',
                'ENABLED',
                'APPROVED',
            ].includes(parent.statusId)
        ) {
            return false;
        }
        if (
            statusId === 'PENDING_APPROVAL' &&
            !['PENDING_APPROVAL', 'ON_HOLD', 'ENABLED', 'APPROVED'].includes(
                parent.statusId
            )
        ) {
            return false;
        }
        if (
            statusId === 'ON_HOLD' &&
            !['ON_HOLD', 'ENABLED', 'APPROVED'].includes(parent.statusId)
        ) {
            return false;
        }
        if (
            statusId === 'ENABLED' &&
            !['ENABLED', 'APPROVED'].includes(parent.statusId)
        ) {
            return false;
        }
        if (
            statusId === 'APPROVED' &&
            !['APPROVED'].includes(parent.statusId)
        ) {
            return false;
        }
    }
    return true;
}

export function getParentClients(filterQuery) {
    const filterFields = {
        filter: {
            order: ['name ASC'],
            fields: ['id', 'name'],
        },
    };

    const query = _.merge({}, filterFields, { filter: filterQuery });
    return ClientService.find(query);
}

export function updateAutomatedOrdering(clientId, value) {
    return ClientService.patch(clientId, value);
}

export function getClientLaborRatesByClientId(id) {
    const now = new Date();
    const filter = {
        where: {
            active: true,
            clientId: id,
            effectiveEndDate: { gt: now },
        },
    };
    return ApiService.get(true, 'clientlaborrates', { filter });
}

export async function isDCIRClient(clientId) {
    if (!clientId) return false;
    const client = await getClient(clientId);

    if (client) {
        const lineOfBusinessSetting = find(client.settings, {
            settingTypeId: 'LINE_OF_BUSINESS',
        });
        if (lineOfBusinessSetting) {
            return (
                lineOfBusinessSetting.setting.segment === 'ONLINE_DISTRIBUTORS'
            );
        }
    }
    return false;
}

export function getWarrantyAssetsByOrderLookup(
    clientId,
    orderLookupValue,
    laborWarrantyId
) {
    return ClientService.getWarrantyAssetsByOrderLookup(
        clientId,
        orderLookupValue,
        laborWarrantyId
    );
}

export function getLaborWarrantyProducts(clientId) {
    return ClientService.getLaborWarrantyProducts(clientId);
}

export function isClientPhotoVerificationEnabledForWorkOrders(workOrder) {
    const { clientId, tradeTypeId, tradeSubTypeId, workOrderTypeId } =
        workOrder;
    return ClientService.isClientPhotoVerificationEnabledForWorkOrders(
        clientId,
        tradeTypeId,
        tradeSubTypeId,
        workOrderTypeId
    );
}

export function registerNewAccountForClient(clientId, firstName) {
    return ClientService.registerNewAccountForClient(clientId, firstName);
}
